import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSpinner, faArrowLeft, faEdit, faIdCard, faGraduationCap, faComments, faCog, faCreditCard, faUserSecret, faTrash, faBook, faEnvelope } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { startImpersonation } from '@/services/impersonation';
import { handleAPIError, showErrorToast, showSuccessToast } from '@/utils/toasts';
import { useUser } from '@/contexts/UserContext';
import FullscreenError from '@/components/utils/FullscreenError';
import AdminEditUserModal from '@/components/admin/users/AdminEditUserModal';
import UserDetailPageTabDetails from '@/components/admin/users/UserDetailPageTabDetails';
import UserDetailPageTabAcademy from '@/components/admin/users/UserDetailPageTabAcademy';
import UserDetailPageTabChats from '@/components/admin/users/UserDetailPageTabChats';
import UserDetailPageTabFeatures from '@/components/admin/users/UserDetailPageTabFeatures';
import UserDetailPageTabSubscriptions from '@/components/admin/users/UserDetailPageTabSubscriptions';
import UserDetailPageTabJournal from '@/components/admin/users/UserDetailPageTabJournal';
import UserDetailPageTabMails from '@/components/admin/users/UserDetailPageTabMails';
import type { User, Scratchpad, UserID } from '@/models';

type TabType = 'details' | 'academy' | 'chats' | 'journal' | 'features' | 'subscriptions' | 'mails';

const TABS: { id: TabType; label: string; icon: typeof faIdCard }[] = [
  { id: 'details', label: 'Details', icon: faIdCard },
  { id: 'academy', label: 'Academy', icon: faGraduationCap },
  { id: 'chats', label: 'Chats', icon: faComments },
  { id: 'journal', label: 'Journal', icon: faBook },
  { id: 'features', label: 'Features', icon: faCog },
  { id: 'subscriptions', label: 'Abonnements', icon: faCreditCard },
  { id: 'mails', label: 'Mails', icon: faEnvelope },
];

export default function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser, isAdmin, isStaff } = useUser();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [scratchpad, setScratchpad] = useState<Scratchpad | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!isStaff) {
      navigate('/dashboard');
      return;
    }

    if (userId) {
      fetchUserDetails();
    }
  }, [userId, isStaff, navigate]);

  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      // Get user by ID
      const foundUser = await APIRoutes.getUser(userId);
      setUser(foundUser);

      // Then fetch user-specific data
      const scratchpadData = await APIRoutes.getUserScratchpad(userId).catch(() => null);
      setScratchpad(scratchpadData);
    } catch (err) {
      console.error('Critical error loading user details:', err);
      setError('Backend nicht erreichbar oder schwerwiegender Fehler beim Laden der Benutzerdetails');
      setCriticalError(true);
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserUpdated = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const handleImpersonateUser = async () => {
    if (!userId || !user) return;

    try {
      setIsImpersonating(true);
      const response = await APIRoutes.getUserToken(userId as UserID);
      const userName = `${user.firstName} ${user.lastName}`.trim() || user.email;
      startImpersonation(response.token, userName);
      // Reload the page to refresh the entire app state with the new user
      window.location.href = '/';
    } catch (err) {
      handleAPIError(err);
      showErrorToast('Impersonation fehlgeschlagen');
      setIsImpersonating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userId || !user) return;

    // Prevent self-deletion
    if (userId === currentUser?.id) {
      showErrorToast('Sie können sich nicht selbst löschen');
      return;
    }

    // Confirmation dialog
    if (!confirm('Sind Sie sicher, dass Sie diesen Benutzer löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await APIRoutes.deleteUser(userId as UserID);
      showSuccessToast('Benutzer erfolgreich gelöscht');
      navigate('/admin/users');
    } catch (err) {
      console.error('Error deleting user:', err);
      showErrorToast('Fehler beim Löschen des Benutzers');
      handleAPIError(err);
      setIsDeleting(false);
    }
  };

  if (!isStaff) {
    return null;
  }

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Benutzerdetails nicht verfügbar"
        message={error ?? 'Die Benutzerdetails konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar oder es ist ein Fehler aufgetreten.'}
        onRetry={fetchUserDetails}
        onBack={() => navigate('/admin/users')}
      />
    );
  }

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <Link
          to="/admin/users"
          className="mr-4 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-5 h-5" />
        </Link>
        <div className="flex items-center flex-1">
          <FontAwesomeIcon icon={faUser} className="w-8 h-8 text-blue-600 dark:text-blue-400 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {user ? `${user.firstName} ${user.lastName}` : 'Benutzerdetails'}
          </h1>
        </div>
        {user && isAdmin && (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleImpersonateUser}
              disabled={isImpersonating}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer disabled:cursor-not-allowed"
            >
              {isImpersonating ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faUserSecret} className="w-4 h-4" />
              )}
              <span>Als Nutzer einloggen</span>
            </button>
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer"
            >
              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
              <span>Bearbeiten</span>
            </button>
            <button
              onClick={handleDeleteUser}
              disabled={isDeleting || userId === currentUser?.id}
              title={userId === currentUser?.id ? 'Sie können sich nicht selbst löschen' : 'Benutzer löschen'}
              className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 cursor-pointer disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
              )}
              <span>Löschen</span>
            </button>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      {!loading && user && (
        <nav className="flex space-x-1 mb-6 border-b border-gray-200 dark:border-gray-700">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors cursor-pointer flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600 dark:text-blue-400 dark:border-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      )}

      {loading && (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Lade Benutzerdetails...</p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && user && (
        <>
          {activeTab === 'details' && <UserDetailPageTabDetails user={user} />}
          {activeTab === 'academy' && <UserDetailPageTabAcademy user={user} />}
          {activeTab === 'chats' && <UserDetailPageTabChats user={user} scratchpad={scratchpad} />}
          {activeTab === 'journal' && <UserDetailPageTabJournal user={user} />}
          {activeTab === 'features' && <UserDetailPageTabFeatures user={user} onUserUpdated={handleUserUpdated} />}
          {activeTab === 'subscriptions' && <UserDetailPageTabSubscriptions user={user} />}
          {activeTab === 'mails' && <UserDetailPageTabMails user={user} onUserUpdated={handleUserUpdated} />}
        </>
      )}

      {/* Edit User Modal */}
      {user && (
        <AdminEditUserModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          user={user}
          onUserUpdated={handleUserUpdated}
        />
      )}
    </>
  );
}
